import { Button } from '@ipfabric/ui-components/Button/Button';
import { IconButton } from '@ipfabric/ui-components/IconButton/IconButton';
import { useStableCallback } from '@ipfabric/ui-components/hooks/useStableCallback';
import {
  type ManualLink,
  type PublicSnapshotManualLink,
  createEnumColumn,
  createTextColumn,
} from '@ipfabric/utils';
import { createFrontendOnlyColumn } from 'components/Table/columns/frontendOnlyColumn';
import { Table } from 'components/Table/components/Table';
import type {
  TableActionsItem,
  TableBulkAction,
  TableBulkActionClickEvent,
} from 'components/Table/model/TableModel';
import { useFrontendLocalTable } from 'components/Table/model/useFrontendLocalTable';
import { useSimpleConfirmDialog } from 'containers/ConfirmDialog/useSimpleConfirmDialog';
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  memo,
  useMemo,
  useRef,
} from 'react';
import { useResponsiveTableButton } from '../../utils/useResponsiveTableButton';
import type { PublicManualLink } from './model/ManualLinksModel';

const StatusCell: FC<{
  status: 'valid' | 'invalid';
}> = ({ status }) => {
  const isValid = status === 'valid';
  return (
    <div
      className={`text-sm px-1.5 rounded-sm ${isValid ? 'bg-status-success text-status-on-success' : 'bg-status-error text-status-on-error'}`}
    >
      {isValid ? 'Valid' : 'Invalid'}
    </div>
  );
};

interface Props {
  isSnapshotSpecific: boolean;
  loading: boolean;
  manualLinks: PublicManualLink[];
  onDeleteMany: (ids: Set<string>) => void;
  onUseGlobalSettings: () => void;
  setEditedItem: Dispatch<SetStateAction<PublicManualLink | 'new' | null>>;
}

const BareManualLinksList: FC<Props> = (props) => {
  const confirmDeleteDialog = useSimpleConfirmDialog();
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { actionItem: overrideActionItem } = useResponsiveTableButton({
    label: 'Overwrite with global settings',
    icon: 'fa-arrows-rotate',
    onClick: props.onUseGlobalSettings,
    containerRef: tableContainerRef,
    size: 'small',
    show: props.isSnapshotSpecific,
  });

  const requestManualLinkDelete = useStableCallback(
    (row: ManualLink): Promise<boolean> =>
      requestDeleteEntries(
        new Set([row.id]),
        `'${row.srcDeviceSn}' - '${row.dstDeviceSn}'`,
      ),
  );

  const requestDeleteEntries = useStableCallback(
    async (ids: Set<string>, label?: string): Promise<boolean> => {
      if (
        await confirmDeleteDialog.show(
          {
            header:
              ids.size === 1
                ? 'Manual device link will be deleted'
                : 'Manual device links will be deleted',
            subHeader: label
              ? `Do you really want to delete ${label}?`
              : `Do you really want to delete ${ids.size} selected manual links?`,
            btnConfirm: 'Delete',
            btnSecondaryConfirm: 'Cancel',
          },
          {
            btnConfirmDataTestId: 'manual-links-dialog-remove-confirm',
          },
        )
      ) {
        props.onDeleteMany(ids);
        return true;
      }
      return false;
    },
  );

  const columns = useMemo(
    () => [
      createTextColumn({
        key: 'id',
        title: 'ID',
        hidden: true,
      }),
      createTextColumn({
        key: 'srcDeviceSn',
        title: 'Device (from)',
      }),
      createTextColumn({
        key: 'srcInterface',
        title: 'Interface (from)',
      }),
      createTextColumn({
        key: 'dstDeviceSn',
        title: 'Device (to)',
      }),
      createTextColumn({
        key: 'dstInterface',
        title: 'Interface (to)',
      }),
      createEnumColumn({
        key: 'type',
        title: 'Link type',
        options: {
          L1: 'L1',
          L2: 'L2',
        },
      }),
      ...(props.isSnapshotSpecific
        ? [
            createFrontendOnlyColumn({
              key: 'status',
              title: 'Status',
              description:
                'Manual link status availability depends on the selected snapshot.',
              render: (row: PublicSnapshotManualLink) =>
                row.status ? <StatusCell status={row.status} /> : undefined,
            }),
            createFrontendOnlyColumn({
              key: 'statusCode',
              title: 'Status message',
              description:
                'Manual link status code availability depends on the selected snapshot.',
              render: (row: PublicSnapshotManualLink) => row.statusCode,
            }),
          ]
        : []),
      ...[
        createFrontendOnlyColumn({
          key: 'actions',
          title: 'Actions',
          disabledSort: true,
          width: 81,
          fixedWidth: true,
          render: (row: ManualLink) => {
            return (
              <>
                <IconButton
                  dataTestId={`manual-links-${row.srcInterface}-${row.dstInterface}-${row.srcDeviceSn}-${row.dstDeviceSn}-edit-icon`}
                  icon='fa-pen'
                  onClick={() => {
                    const editedItem = props.manualLinks.find(
                      (item) => item.id === row.id,
                    );
                    props.setEditedItem(editedItem ?? null);
                  }}
                  variant='plain'
                />
                <IconButton
                  dataTestId={`manual-links-${row.srcInterface}-${row.dstInterface}-${row.srcDeviceSn}-${row.dstDeviceSn}-remove-icon`}
                  accent='negative'
                  icon='fa-trash-can'
                  onClick={() => requestManualLinkDelete(row)}
                  type='button'
                  variant='plain'
                />
              </>
            );
          },
        }),
      ],
    ],
    [props.setEditedItem, props.manualLinks, props.isSnapshotSpecific],
  );

  const frontendSimpleFilter = useFrontendLocalTable({
    columns,
    data: props.manualLinks,
  });

  const bulkActions: TableBulkAction[] = [
    {
      key: 'delete',
      title: 'Delete',
      accent: 'negative',
      disableAutoDeselect: true,
      icon: 'fa-trash-can',
      onClick: async ({
        selectedRows,
        deselectAll,
      }: TableBulkActionClickEvent) => {
        const stringIds = new Set([...selectedRows].map(String));
        if (await requestDeleteEntries(stringIds)) {
          deselectAll();
        }
      },
    },
  ];

  const menuItems = useMemo(
    () => [
      {
        standalone: true,
        elementWithKey: (
          <Button
            dataTestId='manual-links-add-link'
            icon='fa-plus-circle'
            key='add-new'
            onClick={() => {
              props.setEditedItem('new');
            }}
            size='small'
            variant='outline'
          >
            Add new
          </Button>
        ),
      } satisfies TableActionsItem,
      overrideActionItem,
    ],
    [props.setEditedItem, overrideActionItem],
  );

  return (
    <>
      <div className='flex flex-col flex-1' ref={tableContainerRef}>
        <Table
          bulkActions={bulkActions}
          className='flex-1 bg-transparent'
          columns={columns}
          loadRowIdsForSelectAll='fromData'
          rowHeight={44}
          rowIdField='id'
          title='Link list'
          disableTableRowDetail
          loading={props.loading}
          menuItems={menuItems}
          {...frontendSimpleFilter}
        />
      </div>
      {confirmDeleteDialog.render()}
    </>
  );
};

export const ManualLinksList = memo(BareManualLinksList);
